import { Box, MenuItem, MenuList, Paper } from '@mui/material';
import { CustomCell, GridCellKind } from '@glideapps/glide-data-grid';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React from 'react';
import { formatDateLocal } from 'utils/helperFunctions';
import { SelectOption } from '@/apis/business/agent/worksheet/types';
import { getSelectColor } from './worksheetSelectColors';

export type SingleSelectCellData = {
  name: 'single_select';
  value: string;
  options: SelectOption[];
};

export type DatetimeCellData = {
  name: 'datetime';
  value: string;
};

const ICON_SIZE = 16;
const SELECT_ICON_SIZE = 12;
const ICON_RIGHT_PADDING = 10;
const ICON_GAP = 8;
const ICON_COLOR = '#6B7280';

const drawPill = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  colorKey?: string,
  maxWidth?: number
) => {
  if (!label) return;

  const color = getSelectColor(colorKey);
  const paddingX = 10;
  const badgeHeight = 22;
  const textWidth = ctx.measureText(label).width;
  const availableWidth = maxWidth ?? width - 16;
  const badgeWidth = Math.min(textWidth + paddingX * 2, availableWidth);
  const badgeX = x + 8;
  const badgeY = y + (height - badgeHeight) / 2;
  const radius = badgeHeight / 2;

  ctx.fillStyle = color.background;
  ctx.beginPath();
  ctx.moveTo(badgeX + radius, badgeY);
  ctx.arcTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + badgeHeight, radius);
  ctx.arcTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX, badgeY + badgeHeight, radius);
  ctx.arcTo(badgeX, badgeY + badgeHeight, badgeX, badgeY, radius);
  ctx.arcTo(badgeX, badgeY, badgeX + badgeWidth, badgeY, radius);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = color.text;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = '12px Inter, sans-serif';
  ctx.fillText(label, badgeX + paddingX, badgeY + badgeHeight / 2, badgeWidth - paddingX * 2);
};

const drawCalendarIcon = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number) => {
  const size = ICON_SIZE;
  const x = centerX - size / 2;
  const y = centerY - size / 2;
  const radius = 2;

  ctx.save();
  ctx.strokeStyle = ICON_COLOR;
  ctx.fillStyle = ICON_COLOR;
  ctx.lineWidth = 1.25;
  ctx.lineJoin = 'round';

  // Calendar body
  ctx.beginPath();
  ctx.moveTo(x + radius, y + 3);
  ctx.lineTo(x + size - radius, y + 3);
  ctx.quadraticCurveTo(x + size, y + 3, x + size, y + 3 + radius);
  ctx.lineTo(x + size, y + size - radius);
  ctx.quadraticCurveTo(x + size, y + size, x + size - radius, y + size);
  ctx.lineTo(x + radius, y + size);
  ctx.quadraticCurveTo(x, y + size, x, y + size - radius);
  ctx.lineTo(x, y + 3 + radius);
  ctx.quadraticCurveTo(x, y + 3, x + radius, y + 3);
  ctx.closePath();
  ctx.stroke();

  // Top binding bar
  ctx.beginPath();
  ctx.moveTo(x, y + 6);
  ctx.lineTo(x + size, y + 6);
  ctx.stroke();

  // Hangers
  ctx.beginPath();
  ctx.moveTo(x + 4, y + 1);
  ctx.lineTo(x + 4, y + 4.5);
  ctx.moveTo(x + size - 4, y + 1);
  ctx.lineTo(x + size - 4, y + 4.5);
  ctx.stroke();

  // Date dots
  const dotY = y + 10.5;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.arc(x + 4.5 + i * 3.5, dotY, 0.9, 0, Math.PI * 2);
    ctx.fill();
  }
  for (let i = 0; i < 2; i++) {
    ctx.beginPath();
    ctx.arc(x + 4.5 + i * 3.5, dotY + 3.5, 0.9, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
};

const drawSelectChevron = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number) => {
  // Compact dropdown chevron — narrow, menu-bar proportions
  const halfW = 3.5;
  const halfH = 2.25;

  ctx.save();
  ctx.strokeStyle = ICON_COLOR;
  ctx.lineWidth = 1.25;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(centerX - halfW, centerY - halfH / 2);
  ctx.lineTo(centerX, centerY + halfH / 2);
  ctx.lineTo(centerX + halfW, centerY - halfH / 2);
  ctx.stroke();
  ctx.restore();
};

export const singleSelectRenderer = {
  kind: GridCellKind.Custom as const,

  isMatch: (cell: CustomCell<any>): cell is CustomCell<SingleSelectCellData> => {
    return cell.kind === GridCellKind.Custom && cell.data?.name === 'single_select';
  },

  draw: (args: any) => {
    const { cell, ctx, rect } = args;
    const options: SelectOption[] = cell.data?.options || [];
    const selected = options.find((option) => option.id === cell.data?.value);
    const iconCenterX = rect.x + rect.width - ICON_RIGHT_PADDING - SELECT_ICON_SIZE / 2;
    const iconCenterY = rect.y + rect.height / 2;
    const contentMaxWidth = rect.width - SELECT_ICON_SIZE - ICON_RIGHT_PADDING - ICON_GAP - 8;

    if (selected) {
      drawPill(ctx, rect.x, rect.y, rect.width, rect.height, selected.label, selected.color, contentMaxWidth);
    }

    drawSelectChevron(ctx, iconCenterX, iconCenterY);
    return true;
  },

  provideEditor: () => ({
    editor: (props: any) => {
      const { value, onChange, onFinishedEditing, target } = props;
      const options: SelectOption[] = value.data?.options || [];

      return (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: target?.height ?? 34,
            left: 0,
            minWidth: Math.max(180, target?.width ?? 180),
            maxHeight: 240,
            overflow: 'auto',
            zIndex: 2
          }}
        >
          <MenuList dense>
            <MenuItem
              onClick={() => {
                const next = {
                  ...value,
                  data: { ...value.data, value: '' },
                  copyData: ''
                };
                onChange(next);
                onFinishedEditing(next);
              }}
            >
              <Box color='text.secondary'>Clear</Box>
            </MenuItem>
            {options.map((option) => {
              const color = getSelectColor(option.color);
              return (
                <MenuItem
                  key={option.id}
                  selected={option.id === value.data?.value}
                  onClick={() => {
                    const next = {
                      ...value,
                      data: { ...value.data, value: option.id },
                      copyData: option.label
                    };
                    onChange(next);
                    onFinishedEditing(next);
                  }}
                >
                  <Box
                    px={1}
                    py={0.25}
                    borderRadius='999px'
                    bgcolor={color.background}
                    color={color.text}
                    fontSize={12}
                    fontWeight={500}
                  >
                    {option.label}
                  </Box>
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      );
    },
    disablePadding: true,
    disableStyling: true,
    styleOverride: {
      overflow: 'visible',
      // Keep overlay anchored to the cell but let the menu paint below it
      background: 'transparent',
      boxShadow: 'none'
    }
  }),

  onPaste: (val: string, data: SingleSelectCellData) => {
    const matched = data.options.find(
      (option) => option.id === val || option.label.toLowerCase() === val.toLowerCase()
    );
    return {
      ...data,
      value: matched?.id || ''
    };
  }
};

export const datetimeRenderer = {
  kind: GridCellKind.Custom as const,

  isMatch: (cell: CustomCell<any>): cell is CustomCell<DatetimeCellData> => {
    return cell.kind === GridCellKind.Custom && cell.data?.name === 'datetime';
  },

  draw: (args: any) => {
    const { cell, ctx, rect, theme } = args;
    const raw = cell.data?.value;
    const display = raw ? formatDateLocal(raw) : '';
    const iconCenterX = rect.x + rect.width - ICON_RIGHT_PADDING - ICON_SIZE / 2;
    const iconCenterY = rect.y + rect.height / 2;

    if (display && display !== 'Invalid Date') {
      const textMaxWidth = rect.width - theme.cellHorizontalPadding - ICON_SIZE - ICON_RIGHT_PADDING - ICON_GAP;
      ctx.fillStyle = theme.textDark;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.font = `${theme.baseFontStyle} ${theme.fontFamily}`;
      ctx.fillText(display, rect.x + theme.cellHorizontalPadding, rect.y + rect.height / 2, textMaxWidth);
    }

    drawCalendarIcon(ctx, iconCenterX, iconCenterY);
    return true;
  },

  provideEditor: () => ({
    editor: DatetimeCellEditor,
    disablePadding: true,
    disableStyling: true,
    styleOverride: {
      overflow: 'visible',
      background: 'transparent',
      boxShadow: 'none'
    }
  }),

  onPaste: (val: string, data: DatetimeCellData) => {
    const parsed = dayjs(val);
    return {
      ...data,
      value: parsed.isValid() ? parsed.toISOString() : data.value
    };
  }
};

function DatetimeCellEditor(props: any) {
  const { value, onChange, onFinishedEditing } = props;
  const initial = value.data?.value ? dayjs(value.data.value) : null;
  const [localValue, setLocalValue] = React.useState(initial?.isValid() ? initial : null);
  const [open, setOpen] = React.useState(true);

  const commitValue = (nextValue: dayjs.Dayjs | null, finish: boolean) => {
    const iso = nextValue?.isValid() ? nextValue.toISOString() : '';
    const next = {
      ...value,
      data: { ...value.data, value: iso },
      copyData: iso ? formatDateLocal(iso) : ''
    };
    onChange(next);
    if (finish) onFinishedEditing(next);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Invisible anchor so only the previous DateTimePicker popper (calendar/clock) is visible */}
      <Box
        className='click-outside-ignore'
        sx={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute', pointerEvents: 'none' }}
      >
        <DateTimePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={localValue}
          ampm
          timeSteps={{ minutes: 1 }}
          closeOnSelect={false}
          slotProps={{
            textField: { size: 'small' },
            actionBar: { actions: ['clear', 'accept'] },
            popper: {
              className: 'click-outside-ignore',
              placement: 'bottom-start',
              modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
              sx: {
                zIndex: 20000,
                '& .MuiPaper-root': {
                  transform: 'scale(0.88)',
                  transformOrigin: 'top left'
                },
                // Drop MUI's bottom spacer that pins the selected hour to the top
                // and leaves empty scroll room under the list.
                '& .MuiMultiSectionDigitalClockSection-root::after': {
                  display: 'none'
                },
                // AM/PM hours are [12, 1, …, 11]; move 12 to the bottom → [1, …, 11, 12].
                '& .MuiMultiSectionDigitalClockSection-root:first-of-type': {
                  display: 'flex',
                  flexDirection: 'column',
                  '& > .MuiMultiSectionDigitalClockSection-item:first-of-type': {
                    order: 1,
                    marginTop: '2px'
                  },
                  '& > .MuiMultiSectionDigitalClockSection-item:nth-of-type(2)': {
                    marginTop: '4px'
                  }
                }
              }
            }
          }}
          onChange={(newValue) => {
            setLocalValue(newValue);
            commitValue(newValue, false);
          }}
          onAccept={(newValue) => {
            setLocalValue(newValue);
            setOpen(false);
            commitValue(newValue, true);
          }}
        />
      </Box>
    </LocalizationProvider>
  );
}

export const getColumnTypeIcon = (dataType?: string): string => {
  switch (dataType) {
    case 'app_execution':
      return 'customWorkflowIcon';
    case 'action':
      return 'customActionIcon';
    case 'number':
      return 'customNumberIcon';
    case 'single_select':
      return 'customSelectIcon';
    case 'datetime':
      return 'customDateTimeIcon';
    default:
      return 'customTextIcon';
  }
};
